<!-- =========================================================================================
    Description: Multi File Drag and Drop Selection Component
    Author: @letstream (Letstream Ventures Pvt Ltd, India)
    License: Proprietary

    Disclaimer: Use of this component without the explicit written permissions from
    Letstream Ventures Pvt Ltd is not allowed and is subjected to be prosecuted.
========================================================================================== -->

<template>
    <div class="flex">
        <div
            @dragleave="dragleave"
            @dragover="dragover"
            @drop="do_files_selected('drop', $event)"
            @click="$refs.file_input.click()"
            class="drop-area"
            :class="{
                'w-1/2': show_seperate_camera
            }"
        >
            <vs-button
                type="border"
                color="primary"
                class="w-full"
                :disabled="submit_disabled"                            
            >                                                
                <label class="drop-area-label">
                    <template v-if="is_dropping">
                        Drop here to upload
                    </template>
                    <template v-else>
                        {{ upload_label }}
                    </template>
                </label>
            </vs-button> 
            <input type="file" :multiple="multiple" :accept="allowed_extensions" @change="do_files_selected('input', $event)" ref="file_input" class="hidden" />
        </div>
        <div
            v-if="show_seperate_camera"
            @click="captureFromCamera"
            class="drop-area w-1/2"
        >
            <vs-button
                type="border"
                color="primary"
                class="w-full"
                :disabled="submit_disabled"                            
            >                                                
                <label class="drop-area-label">
                    Capture from Camera
                </label>
            </vs-button> 
            <input type="file" :multiple="multiple" capture accept="image/*" @change="do_files_selected('input', $event, 'camera')" ref="file_input_2" class="hidden" />
        </div>
        
    </div>
</template>

<script>
import { Camera, CameraResultType, CameraSource, CameraDirection } from '@capacitor/camera';

export default {
    props: {
        extensions : {
            type: Array,
        },
        disabled: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: false
        },
        upload_label: {
            type: String,
            default: "Drop or Click to Select Files"
        }
    },
    data() {
        return {
            extensions_parsed: [],
            submit_disabled: this.disabled,
            is_dropping: false
        }
    },
    mounted() {
        this.make_extensions_string()      
    },
    computed: {
        allowed_extensions() {return this.extensions_parsed.join(",")},
        show_seperate_camera() { return this.is_image && window.cordova },
        is_image() {
            return this.extensions_parsed.filter(v => v == 'image/jpeg' || v == 'image/png' || v == 'image/*').length > 0
        }
    },
    watch: {
        disabled(newV) {
            this.submit_disabled = newV
        },
        extensions() {
            this.make_extensions_string()
        }
    },
    methods: {
        get_extension_string(v) {
            if(v == 'csv') {
                return "text/csv"
            } else if (v == 'jpeg' || v == 'jpg') {
                return "image/jpeg"
            } else if (v == 'png') {
                return "image/png"
            } else if (v == 'xls') {
                return "application/vnd.ms-excel"
            } else if (v == 'xlsx') {
                return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            } else if (v == 'pdf') {
                return "application/pdf"
            } else if (v == 'doc') {
                return "application/msword"
            } else if (v == 'docx') {
                return "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            } else if (v == 'ppt') {
                return "application/vnd.ms-powerpoint"
            } else if (v == 'pptx') {
                return "application/vnd.openxmlformats-officedocument.presentationml.presentation"
            } else if (v == 'txt') {
                return "text/plain"
            } else if (v == 'rtf') {
                return "text/rtf"
            } else if (v == 'odt') {
                return "application/vnd.oasis.opendocument.text"
            } else if (v == 'ods') {
                return "application/vnd.oasis.opendocument.spreadsheet"
            } else if (v == 'odp') {
                return "application/vnd.oasis.opendocument.presentation"
            } else if (v == 'image/*') {
                return "image/*";
            }
            return "";
        },
        make_extensions_string() {
            this.extensions.forEach(v => { 
                let c = this.get_extension_string(v);
                if(c != "")
                    this.extensions_parsed.push(c);
            })
        },
        dragleave(e) {
            e.preventDefault();
            this.is_dropping = false
        },
        dragover(e) {
            e.preventDefault();
            this.is_dropping = true
        },
        do_files_selected(upload_method, event, source='input') {
            this.is_dropping = false
            if (upload_method != 'native')
                event.preventDefault();
            let files = []
            
            if (upload_method == 'drop')
                files = event.dataTransfer.files
            else if (upload_method == 'input') 
                files = event.target.files
            else if (upload_method == 'native')
                files = event.files
            
            let finalised_files = []

            console.log("Processing Files ", files, " upload = ", upload_method)

            for(let i=0; i<files.length; i++) {
                let file = files[i]

                if(!this.extensions_parsed.includes(file.type)){
                    this.$vs.notify({
                        time: 2500,
                        title: 'Error',
                        text: `File ${file.name} is not supported. Only extensions ${this.extensions.join(", ")} are supported.`,
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                } else{
                    finalised_files.push(file)
                    if (!this.multiple) break
                }
            }

            console.log("Finalised files" , finalised_files)

            if (finalised_files.length)
                this.$emit("input", {files: this.multiple ? finalised_files : finalised_files[0], source});
        },
        async captureFromCamera() {
            if (!window.cordova)
                this.$refs.file_input_2.click()
            else {
                let image = await Camera.getPhoto({
                    quality: 100,
                    allowEditing: false,
                    resultType: CameraResultType.DataUrl,
                    saveToGallery: false,
                    correctOrientation: true,
                    source: CameraSource.Camera,
                    direction: CameraDirection.Rear
                })
                console.log("Got image ", image)
                this.do_files_selected('native', {files: [this.convertDataURLToFile(image.dataUrl, image.format), ]}, 'camera')
            }
        },
        convertDataURLToFile(dataURL, format) {
            let parts = dataURL.split(';base64,');
            let contentType = parts[0].split(':')[1];
            let raw = window.atob(parts[1]);
            let rawLength = raw.length;
            let uInt8Array = new Uint8Array(rawLength);

            for (let i = 0; i < rawLength; ++i) {
                uInt8Array[i] = raw.charCodeAt(i);
            }

            let blob = new Blob([uInt8Array], { type: contentType });

            console.log("Converted to lob ", blob)

            let f = new File([blob], "captureImage.jpeg", {type: `image/${format}`})
            console.log("File ready", f)
            return f
        }
    }
}

</script>

<style lang="scss">
    .drop-area-label > input[type="file"] {
        display: none;
    }
</style>
